import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck } from 'lucide-react'
import { notificationsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { AppNotification } from '@/types'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

// Slow polling by design (SRS: no real-time channel — school-process
// timescales are compatible with refresh).
const POLL_MS = 60_000

function notificationText(n: AppNotification, t: TFunction): string {
  const p = n.payload
  switch (n.type) {
    case 'REVIEW_REQUESTED':
      return t('notifications.types.REVIEW_REQUESTED', { app: p.app_name })
    case 'DPO_VALIDATED':
      return t('notifications.types.DPO_VALIDATED', { app: p.app_name })
    case 'DECISION_ISSUED':
      return t(
        p.decision === 'OVERRIDDEN'
          ? 'notifications.types.DECISION_OVERRIDDEN'
          : 'notifications.types.DECISION_APPROVED',
        { app: p.app_name, verdict: p.verdict ?? '—' }
      )
    case 'SUBSCRIPTION_EXPIRING':
      return t('notifications.types.SUBSCRIPTION_EXPIRING', {
        days: p.days_left,
        date: p.valid_until,
      })
    case 'ASSESSMENT_EXPIRING':
      return t('notifications.types.ASSESSMENT_EXPIRING', {
        app: p.app_name,
        days: p.days_left,
      })
    case 'ASSESSMENT_EXPIRED':
      return t('notifications.types.ASSESSMENT_EXPIRED', { app: p.app_name })
    case 'ENGINE_BUMP':
      return t('notifications.types.ENGINE_BUMP', { count: p.count })
    case 'INCIDENT_OPENED':
      return t('notifications.types.INCIDENT_OPENED', { app: p.app_name })
    case 'INCIDENT_DPO_ANALYZED':
      return t('notifications.types.INCIDENT_DPO_ANALYZED', { app: p.app_name })
    case 'INCIDENT_DECIDED':
      return t(
        p.decision === 'REVOCA'
          ? 'notifications.types.INCIDENT_REVOKED'
          : 'notifications.types.INCIDENT_RESTORED',
        { app: p.app_name }
      )
    case 'INCIDENT_LIFTED':
      return t('notifications.types.INCIDENT_LIFTED', { app: p.app_name })
    default:
      return n.type
  }
}

const REGISTRY_NOTIFICATIONS = new Set([
  'ENGINE_BUMP', 'INCIDENT_OPENED', 'INCIDENT_DPO_ANALYZED',
  'INCIDENT_DECIDED', 'INCIDENT_LIFTED',
])

function targetPath(n: AppNotification): string {
  if (n.type === 'SUBSCRIPTION_EXPIRING') return '/school'
  if (REGISTRY_NOTIFICATIONS.has(n.type)) return '/registro'
  return n.payload.assessment_id ? `/assessment/${n.payload.assessment_id}` : '/'
}

function relativeTime(iso: string, locale: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const minutes = Math.round(diffMs / 60_000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  if (minutes < 60) return rtf.format(-minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (hours < 24) return rtf.format(-hours, 'hour')
  return rtf.format(-Math.round(hours / 24), 'day')
}

export default function NotificationBell() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: POLL_MS,
  })

  const { data: notifications } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsApi.list({ limit: 15 }),
    enabled: open,
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['notifications'] })

  const markRead = useMutation({
    mutationFn: notificationsApi.markRead,
    onSettled: invalidate,
  })
  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSettled: invalidate,
  })

  const count = unread?.count ?? 0

  const handleClick = (n: AppNotification) => {
    if (!n.read_at) markRead.mutate(n.id)
    setOpen(false)
    navigate(targetPath(n))
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-1.5 rounded hover:bg-white/10 transition-colors"
          aria-label={t('notifications.title') + (count > 0 ? ` (${count})` : '')}
        >
          <Bell className="w-4 h-4 text-blue-100" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-mim-orange text-white text-[10px] font-bold leading-4 text-center"
              aria-hidden="true"
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold">{t('notifications.title')}</span>
          {count > 0 && (
            <button
              onClick={() => markAllRead.mutate()}
              className="flex items-center gap-1 text-xs text-mim-blue dark:text-primary hover:underline"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {!notifications?.length ? (
            <p className="px-3 py-6 text-sm text-muted-foreground text-center">
              {t('notifications.empty')}
            </p>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={cn(
                  'flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm border-b border-border last:border-b-0 hover:bg-muted transition-colors',
                  !n.read_at && 'bg-mim-lightblue/40 dark:bg-primary/10'
                )}
              >
                <span
                  className={cn(
                    'mt-1.5 w-2 h-2 rounded-full flex-shrink-0',
                    n.read_at ? 'bg-transparent' : 'bg-mim-blue dark:bg-primary'
                  )}
                />
                <span className="flex-1 min-w-0">
                  <span className={cn('block leading-snug', !n.read_at && 'font-medium')}>
                    {notificationText(n, t)}
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {relativeTime(n.created_at, i18n.language)}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
